import { html } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

type TranslationPlaceholders = Record<string, string>;
export type TranslationKey = string;

export class LocalizationService {
  private static instance: LocalizationService;

  private translations: Record<string, string> = {};

  private constructor() {
    /* nope */
  }

  public static getInstance(): LocalizationService {
    if (!LocalizationService.instance) {
      LocalizationService.instance = new LocalizationService();
    }
    return LocalizationService.instance;
  }

  async load(localeToLoad?: string) {
    try {
      const defaultLocale = document.documentElement.lang ?? 'en';
      const locale = localeToLoad ?? defaultLocale;

      this.translations = await import(`../generated/translations/${locale}.json`).then((m) => m.default);
      window.dispatchEvent(new CustomEvent('lua-translations-loaded', { detail: { locale } }));
    } catch (e: unknown) {
      // eslint-disable-next-line no-console
      console.error('Error loading translations:', e);
      const errorMessage = e instanceof Error ? e.message : String(e);
      window.dispatchEvent(new CustomEvent('lua-translations-error', { detail: { error: errorMessage } }));
    }
  }

  getTranslation(key: string, placeholders?: TranslationPlaceholders) {
    let translation = this.translations?.[`${key}`] ?? key;

    // Replace placeholder from string
    if (placeholders) {
      translation = this.translations?.[`${key}`]?.replace(/\${([\w.]+)}/g, (_, placeholderKey) => {
        return placeholders[`${placeholderKey}`] ?? '';
      });
    }

    // check if translation have any HTML markup
    const hasHtmlTags = /<[^>]*>/.test(translation);

    if (hasHtmlTags) {
      return html`${unsafeHTML(translation)}`;
    }

    return translation;
  }
}

// Load translations
const translations = LocalizationService.getInstance();
translations.load();

/**
 * Return the translated string given the translation key
 *
 * Kept for backwards compatibility
 */
export const i18n = (key: TranslationKey, placeholders?: TranslationPlaceholders) => {
  return LocalizationService.getInstance().getTranslation(key, placeholders);
};

declare global {
  interface WindowEventMap {
    'lua-translations-loaded': CustomEvent<{ locale: string }>;
  }
}
